import {
  checkIfUserVerifiedEmailAndSendFirstVerificationEmail,
  isUserDesigner,
  isUserLoggedIn,
  isUserStudent,
  pollHasUserVerifiedEmail,
  sendUserVerificationEmail,
} from '@Libraries/user.library';
import {openCheckoutForCreditPackPurchase} from '@Modals/checkout-modal/checkout-modal.library';
import {updatePanelProperties, updatePanelSizeData} from '@Components/panel/panel-reducer';
import type {RGBA} from '@Utils/color.util';
import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import {LayoutTypes} from '@PosterWhiteboard/items/layouts/layout.types';
import {readCookie, setCookie} from '@Utils/cookie.util';
import {COPY_STYLES_COOKIE, FeatureToShowOnLoad, PosterEditorOnboarding} from '@Components/poster-editor/poster-editor.types';
import {MobilePageSelectionModeState} from '@PosterWhiteboard/poster/poster.types';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {openMessageModal} from '@Modals/message-modal';
import {
  selectIsSinglePageLayout,
  clearItemAiProcessing,
  setHasUserSeenImageEdit,
  setHasUserSeenImageUpscale,
  setItemAiProcessing,
  setMobilePageSelectionModeState,
  updateCopiedStylesState,
  updateIsEmbeddedExportMode,
  updateLayersPanelState,
  updateSidebarState,
  updateVersionHistoryModeState,
} from '@Components/poster-editor/poster-editor-reducer';
import {openLayersModal} from '@Modals/poster-editor-layers-modal/poster-editor-layers-modal';
import {deviceType} from 'react-device-detect';
import {showSaveReminderNotification} from '@Components/save-reminder-notification/save-reminder-notification.helper';
import {useAppSelector} from '@/hooks';
import {EDITOR_AUTOSAVE_ONBOARDING_COOKIE} from '@Hooks/poster-editor/useEditorMobileOnboarding';
import {DEVICE_TYPE} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls/poster-item-controls.types';
import Mousetrap from 'mousetrap';
import type {Canvas, CanvasEvents, FabricObject} from '@postermywall/fabricjs-2';
import {CURSOR_OVERLAY_CONTAINER_ID} from '@Components/poster-editor/components/cursor-overlay/cursor-overlay.types';
import type {GA4EventSources} from '@Libraries/ga-events';
import {GA4EventName, GA4EventParamName, LayersGA4Source, TimelineOpenSource, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {trackTimelineOpen} from '@Libraries/poster-editor-tracking.library';
import {persistMobileTimelineOpenPreference} from '@Libraries/poster-editor.library';
import {PosterModeType} from '@PosterWhiteboard/poster/poster-mode.class';
import {POSTER_CUSTOM, POSTER_CUSTOM_PRINT} from '@Panels/resize-panel/resize-panel.types';
import {hasDatePassed2} from '@Utils/date.util';
import {EDITOR_LEFT_MARGIN, EDITOR_RIGHT_MARGIN, EDITOR_RIGHT_MARGIN_SMALL} from '@Components/poster-editor/library/poster-editor-scrolling-library';
import {
  AiProcessingStatus,
  AnimatedItemLoadingOverlayVariant,
  type CopyableItemStyles,
  ItemLoadingOverlayType,
  ItemLoadingReason,
  type ItemType,
} from '@PosterWhiteboard/items/item/item.types';
import type {BuyCreditsResponse} from '@/types/credits.types';
import {loadImageAsync} from '@Utils/image.util';
import {isPremiumPlusUser} from '@Utils/user.util';
import type {ImageItem} from '@PosterWhiteboard/items/image-item/image-item.class';
import {openPosterAddItemsModal} from '@Modals/poster-add-items-modal';
import {scrollToPage} from '@Components/poster-editor/library/poster-editor-page-library';
import {AddItemOptions} from '@Panels/poster-add-items-panel/poster-add-items-panel';
import type {Page} from '@PosterWhiteboard/page/page.class';
import {ItemAddType} from '@PosterWhiteboard/page/page.types';
import {isTransparentColorBackground} from '@PosterWhiteboard/page/background/background.types';
import {CUSTOM_CONTROLS_WRAPPER} from '@PosterWhiteboard/libraries/custom-item-controls.library';
import {v4 as uuidv4} from 'uuid';
import {createItemFromObject} from '@PosterWhiteboard/items/item/item-factory';
import {canUserAccessBrandKits, openAppropriateUpsellingDialogForBrandKits} from '@Libraries/brands-library';
import {waitForUserImageVariantsReady} from '@Libraries/user-image-library';
import {EmbeddedEditorMessageType} from '@Libraries/embedded-editor';
import {onClickRemoveVideoBG} from '@Components/poster-editor/library/poster-editor-open-modals';
import {getTeamCredits, refreshTeamCreditsInStore} from '@Components/team-credits/team-credits-library';
import {openAiRemoveBgModal} from '@Modals/ai-remove-bg-modal/ai-remove-bg-modal.types';
import {loadPosterVersionFromHashedId} from '@PosterWhiteboard/poster/poster-init';
import {onPosterEditorVersionHistoryModal} from '@Modals/poster-editor-version-history-modal/poster-editor-version-history-modal';
import type {UserHasSeenResponse} from '@Components/social-media/social-media.types';
import type {ErrorEmailVerificationRequired} from '@Panels/user-email-verification-panel/user-email-verification-panel.types';
import {openUserEmailVerificationModal} from '@Modals/user-email-verification-modal/user-email-verification-modal.types';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {openUserEmailVerificationErrorModal} from '@Modals/user-email-verification-error-modal/user-email-verification-error-modal.types';
import {removeItemLoading, startItemLoading, transferItemLoadingStates} from '@/libraries/item-loading.library';
import {isEditorMobileVariant} from '@Libraries/is-editor-mobile-variant';
import {isInReviewOrGalleryTemplate} from '@Libraries/poster-editor.library';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import {
  checkIfUserAllowedMultipageSubmissions,
  openMultipageTemplateSubmissionUnavailableModal,
} from '@Modals/multipage-template-submission-unavailable-modal/multipage-template-submission-unavailable-modal.library';
import {applyCanvasCursor, setCanvasCursor} from '@Utils/setCanvasCursor';
import tinycolor from 'tinycolor2';
import {findPageCanvasBackdropElement} from '@PosterWhiteboard/page/page-dom.util';

export const EDITOR_SIDEBAR_WIDTH = 310;

/** Hotspot offset baked into the pick-mode cursor URLs, as `x y` in CSS cursor syntax. */
const CURSOR_HOTSPOT = '0 16';
/** Lets fabric settle its own cursor handling for the click that opened the mode before we override it. */
const CURSOR_APPLY_DELAY_MS = 10;

type CursorModeEvent = CanvasEvents['mouse:move'] | MouseEvent | KeyboardEvent;

/** Teardown for the single in-progress canvas-pick mode; null when none is active. */
let activeCursorModeDisposer: (() => void) | null = null;

export const undo = (): void => {
  void navigatePosterHistoryAfterTextEdit(async (whiteboard) => {
    if (whiteboard.history.hasUndo()) {
      void whiteboard.pause();
    }
    await whiteboard.history.undo();
  });
};

export const redo = (): void => {
  void navigatePosterHistoryAfterTextEdit(async (whiteboard) => {
    if (whiteboard.history.hasRedo()) {
      void whiteboard.pause();
    }
    await whiteboard.history.redo();
  });
};

/**
 * Typing is committed to poster history on a debounce, so an in-progress text edit is committed and closed first —
 * otherwise undo would skip the latest typing and the late commit would land on top of the restored state.
 */
const navigatePosterHistoryAfterTextEdit = async (navigateHistory: (whiteboard: Poster) => Promise<void>): Promise<void> => {
  const whiteboard = window.posterEditor?.whiteboard;
  if (!whiteboard) {
    return;
  }
  await whiteboard.getCurrentPage().exitTextEditModeCommittingText();
  await navigateHistory(whiteboard);
};

export const onAddBrandAssetClickedHandler = async (openInNewTab = false): Promise<void> => {
  if (!canUserAccessBrandKits()) {
    openAppropriateUpsellingDialogForBrandKits(true);
    return;
  }
  const url = window.PMW.util.site_url(`posters/mine#/brands`);

  if (openInNewTab) {
    window.open(url, '_blank');
    return;
  }

  await window.posterEditor?.whiteboard?.savePoster.save();
  window.location.href = url;
};

export const getColorsOnMyDesign = (): string[] => {
  const pages = window.posterEditor?.whiteboard?.pages.getPagesInOrder() ?? [];
  const colors = pages.flatMap((page) => page.getColorsOnPage());
  const uniqueColors = Array.from(new Set(colors));
  return uniqueColors.sort((a, b) => {
    return tinycolor(a).getLuminance() - tinycolor(b).getLuminance();
  });
};

export const pausePosterOnModalOpen = (): void => {
  void window.posterEditor?.whiteboard?.pause();
};

export const setAutoSaveOnboardingCookie = (): void => {
  setCookie(EDITOR_AUTOSAVE_ONBOARDING_COOKIE, '1', 180);
};

export const showAutoSaveOnBoarding = (): boolean => {
  return false;
};

export const onPosterPause = (): void => {
  window.posterEditor?.whiteboard?.pause().catch((e) => {
    console.error(e);
  });
};

export const onPosterPlay = (): void => {
  window.posterEditor?.whiteboard?.play().catch((e) => {
    console.error(e);
  });
};

export const togglePosterPlayback = (shouldPlay: boolean): void => {
  if (shouldPlay) {
    onPosterPlay();
  } else {
    onPosterPause();
  }
};

export const openTimelineFromEditPill = (): void => {
  trackTimelineOpen(TimelineOpenSource.EDIT_TIMELINE_PILL);
  persistMobileTimelineOpenPreference(true);
  window.posterEditor?.whiteboard?.updateIsTimelineMode(true);
};

export const onPosterStop = (): void => {
  void window.posterEditor?.whiteboard?.stop();
};

// TODO: THIS COMPONENT DOES NOT SUPPORT MULTI PAGES FOR NOW
export const onPosterSkipForward = (): void => {
  // TODO: Add logic to turn to next page.
};

export const getNumberOfAudioItemsInPoster = (): number => {
  const audioPlayist = useAppSelector((state) => {
    const currentPageId = state.posterEditor.currentPageId;
    return state.posterEditor.posterObject?.pages?.pagesHashMap?.[currentPageId]?.audioClips;
  });

  if (audioPlayist) {
    return Object.keys(audioPlayist.audioItemsHashMap).length;
  }
  return 0;
};

export const showCopyStylesAsNewFeature = (): boolean => {
  return false;
};

export const onCopyStylesClicked = (): void => {
  const selectedItemDetails = window.posterEditor?.whiteboard?.getCurrentPage().getSelectedItems();
  const cursor = 'images/postermaker/copy-style-brush.png';

  if (selectedItemDetails?.length) {
    // A transparent background has no appearance to carry over; copy-styles is intentionally a no-op there.
    if (isTransparentColorBackground(selectedItemDetails[0])) {
      return;
    }
    const styles: CopyableItemStyles = selectedItemDetails[0].getCopyableStyles();
    window.PMW.redux.store.dispatch(updateCopiedStylesState(styles));
    changeCanvasCursor(cursor, disablePastingStyles);

    setCookie(COPY_STYLES_COOKIE, '1', 180);
    trackPosterBuilderGA4Events(GA4EventName.COPY_STYLES_CLICKED);

    if (deviceType === DEVICE_TYPE.MOBILE) {
      showMessageGrowl({
        text: window.i18next.t('pmwjs_style_copied'),
        interval: 3000,
        type: GROWL_TYPE.NEUTRAL,
        showCloseIcon: false,
        icon: 'icon-copy-style-brush',
      });
    }
  }
};
export const pasteStylesOnMouseDown = async (): Promise<void> => {
  const selectedItems = window.posterEditor?.whiteboard?.getCurrentPage().getSelectedItems();
  const {copiedStyles} = window.PMW.redux.store.getState().posterEditor;
  if (selectedItems && copiedStyles !== undefined) {
    try {
      await Promise.all(
        selectedItems.map(async (selectedItem) => {
          try {
            await selectedItem.pasteStyles(copiedStyles);
          } catch (e) {
            console.error(e);
          }
        })
      );
      if (deviceType === DEVICE_TYPE.MOBILE) {
        showMessageGrowl({
          text: window.i18next.t('pmwjs_style_pasted'),
          interval: 3000,
          type: GROWL_TYPE.NEUTRAL,
          showCloseIcon: false,
          icon: 'icon-paste-style-brush',
        });
      }
    } catch (e) {
      console.error(e);
    }
  }
  window.PMW.redux.store.dispatch(updateCopiedStylesState(undefined));
  const pages = window.posterEditor?.whiteboard?.pages.pagesHashMap;
  if (pages) {
    for (const page of Object.values(pages)) {
      setCanvasCursor(page.fabricCanvas as Canvas, 'default');
    }
  }
};

export const removeBgOnMouseDown = async (): Promise<void> => {
  const selectedItems = window.posterEditor?.whiteboard?.getCurrentPage().getSelectedItems();
  const {isRemoveBGModeEnabled} = window.PMW.redux.store.getState().posterEditor;
  if (isRemoveBGModeEnabled && selectedItems) {
    await removeBgForItem(selectedItems[0]);
  }
};

export const removeBgForItem = async (item: ItemType): Promise<void> => {
  if (item.isImage()) {
    removeImageBackgroundFromId(item.uid);
  } else if (item.isVideo()) {
    const {credits} = await getTeamCredits();
    void onClickRemoveVideoBG(item, credits);
  } else if (item.isSlideshow()) {
    const slide = item.getSelectedSlide();
    if (slide.isImageSlide()) {
      removeImageSlideBackgroundFromId(item.uid, slide.uid);
    } else if (slide.isVideoSlide()) {
      const {credits} = await getTeamCredits();
      void onClickRemoveVideoBG(slide, credits);
    } else {
      showMessageGrowl({
        text: window.i18next.t('pmwjs_select_image_or_video_to_remove_bg'),
        type: GROWL_TYPE.NEUTRAL,
      });
    }
  } else {
    showMessageGrowl({
      text: window.i18next.t('pmwjs_select_image_or_video_to_remove_bg'),
      type: GROWL_TYPE.NEUTRAL,
    });
  }
};

export const deleteItemByID = (uid: string): void => {
  window.posterEditor?.whiteboard?.deleteItemById(uid);
};

export const toggleLockStateForSelectedItem = (): void => {
  const selectedItems = window.posterEditor?.whiteboard?.getCurrentPage().getSelectedItems();

  if (selectedItems?.length !== 1) {
    return;
  }

  void selectedItems[0].updateLock(!selectedItems[0].fabricObject.lockMovementX);
};

export const unlockAllLockedItemsInActiveSelection = (): void => {
  const selectedItems = window.posterEditor?.whiteboard?.getCurrentPage().getSelectedItems();
  if (!selectedItems?.length) {
    return;
  }

  const activeSelection = window.posterEditor?.whiteboard?.getCurrentPage().activeSelection.getActiveObject();
  if (!activeSelection) {
    return;
  }

  activeSelection.set({
    lockMovementX: false,
    lockMovementY: false,
    lockScalingX: false,
    lockScalingY: false,
    lockRotation: false,
  });

  selectedItems.forEach((selectedItem) => {
    if (selectedItem.fabricObject.isLocked()) {
      void selectedItem.updateLock(false);
    }
  });
};

export const lockAllItemsInActiveSelection = (): void => {
  const selectedItems = window.posterEditor?.whiteboard?.getCurrentPage().getSelectedItems();
  if (!selectedItems?.length) {
    return;
  }

  const activeSelection = window.posterEditor?.whiteboard?.getCurrentPage().activeSelection.getActiveObject();
  if (!activeSelection) {
    return;
  }

  activeSelection.set({
    lockMovementX: true,
    lockMovementY: true,
    lockScalingX: true,
    lockScalingY: true,
    lockRotation: true,
  });

  selectedItems.forEach((selectedItem) => {
    if (!selectedItem.fabricObject.isLocked()) {
      void selectedItem.updateLock(true);
    }
  });
};

export const toggleLockStateForActiveSelection = (): void => {
  const selectedItems = window.posterEditor?.whiteboard?.getCurrentPage().getSelectedItems();
  if (!selectedItems?.length) {
    return;
  }
  if (selectedItems.some((item) => item.fabricObject.isLocked())) {
    unlockAllLockedItemsInActiveSelection();
  } else {
    lockAllItemsInActiveSelection();
  }
};

export const duplicateActiveItems = (): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  currentPage?.activeSelection?.onDuplicate();
};

export const ungroupActiveSelection = (): void => {
  window.posterEditor?.whiteboard?.getCurrentPage()?.items.groups.ungroupSelection();
};

export const selectActiveBackground = async (): Promise<void> => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }
  await currentPage.ensureAndSelectActiveBackground();
};

export const removeImageSlideBackgroundFromId = (slideshowId: string, uid: string): void => {
  if (window.PMW.isPAYGUser()) {
    void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_BG_REMOVALS);
    return;
  }

  const slideshow = window.posterEditor?.whiteboard?.getItemForId(slideshowId);

  if (!slideshow?.isSlideshow()) {
    return;
  }

  const item = slideshow.slides.slidesHashMap[uid];

  if (!item?.isImageSlide()) {
    return;
  }

  if (item.removeBackground.isBackgroundBeingRemoved) {
    showMessageGrowl({
      text: window.i18next.t('pmwjs_item_background_removal_is_in_progress'),
      type: GROWL_TYPE.NEUTRAL,
    });
    return;
  }

  if (item.removeBackground.isBackgroundRemoved) {
    showMessageGrowl({
      text: window.i18next.t('pmwjs_item_background_is_removed'),
      type: GROWL_TYPE.NEUTRAL,
    });
    return;
  }

  void item.removeBackground.removeBackground();
};

export const removeImageBackgroundFromId = (uid: string): void => {
  if (window.PMW.isPAYGUser()) {
    void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_BG_REMOVALS);
    return;
  }

  const item = window.posterEditor?.whiteboard?.getItemForId(uid);
  if (!item?.isImage()) {
    return;
  }

  const aiProcessingStatus = window.PMW.redux.store.getState().posterEditor.activeAiProcessing[uid] ?? AiProcessingStatus.NONE;
  if (aiProcessingStatus !== AiProcessingStatus.NONE) {
    return;
  }

  if (item.removeBackground.isBackgroundRemoved) {
    showMessageGrowl({
      text: window.i18next.t('pmwjs_item_background_is_removed'),
      type: GROWL_TYPE.NEUTRAL,
    });
    return;
  }

  if (item.removeBackground.isBackgroundBeingRemoved) {
    showMessageGrowl({
      text: window.i18next.t('pmwjs_item_background_removal_is_in_progress'),
      type: GROWL_TYPE.NEUTRAL,
    });
    return;
  }

  void item.removeBackground.removeBackground();
};

export const removeVideoBackgroundFromId = (uid: string): void => {
  if (window.PMW.isPAYGUser()) {
    void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_BG_REMOVALS);
    return;
  }

  const item = window.posterEditor?.whiteboard?.getItemForId(uid);
  if (!item?.isVideo()) {
    return;
  }

  if (item.removeBackground.isBackgroundRemoved || item.removeBackground.isBackgroundBeingRemoved) {
    return;
  }

  void item.removeBackground.removeBackground();
};

export const removeVideoSlideBackgroundFromId = (slideshowId: string, uid: string): void => {
  if (window.PMW.isPAYGUser()) {
    void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_BG_REMOVALS);
    return;
  }

  const slideshow = window.posterEditor?.whiteboard?.getItemForId(slideshowId);

  if (!slideshow?.isSlideshow()) {
    return;
  }

  const item = slideshow.slides.slidesHashMap[uid];

  if (!item?.isVideoSlide()) {
    return;
  }

  if (item.removeBackground.isBackgroundRemoved || item.removeBackground.isBackgroundBeingRemoved) {
    return;
  }

  void item.removeBackground.removeBackground();
};

interface UpscaleImageItemOptions {
  uid: string;
  gaSource: GA4EventSources;
  aiProcessingStatus: AiProcessingStatus;
  teamCredits: number;
}

const getUpscaleCreditCost = (hasRemovedBg: boolean): number => {
  return hasRemovedBg ? 2 : 1;
};

const handleInsufficientCreditsForUpscale = (options: UpscaleImageItemOptions): void => {
  openCheckoutForCreditPackPurchase({
    headingText: isPremiumPlusUser() ? window.i18next.t('pmwjs_purchase_more_credits_for_ai_image') : window.i18next.t('pmwjs_purchase_more_credits_for_ai_image_with_upgrade_message'),
    modalTitle: window.i18next.t('pmwjs_youre_out_of_credits'),
    onSuccess: (data: BuyCreditsResponse): void => {
      if (data.credits > 0) {
        void refreshTeamCreditsInStore();
        void upscaleImageItem({...options, teamCredits: data.credits, aiProcessingStatus: AiProcessingStatus.NONE});
      }
    },
  });
};

const hasUserSeenAiEditIntroPopup = async (): Promise<boolean> => {
  if (!isUserLoggedIn()) {
    return true;
  }
  try {
    return (await window.PMW.readLocal('ai/hasUserSeenAiEditIntroPopup')) as boolean;
  } catch {
    return true;
  }
};

const fetchHasUserSeenImageUpscale = async (): Promise<boolean> => {
  if (!isUserLoggedIn()) {
    return true;
  }
  try {
    return (await window.PMW.readLocal('ai/hasUserSeenImageUpscale')) as boolean;
  } catch {
    return true;
  }
};

export const ensureHasUserSeenAiFlagsInStore = (): void => {
  if (!isUserLoggedIn()) {
    return;
  }
  void (async (): Promise<void> => {
    const [hasSeenImageEdit, hasSeenImageUpscale] = await Promise.all([hasUserSeenAiEditIntroPopup(), fetchHasUserSeenImageUpscale()]);
    window.PMW.redux.store.dispatch(setHasUserSeenImageEdit(hasSeenImageEdit));
    window.PMW.redux.store.dispatch(setHasUserSeenImageUpscale(hasSeenImageUpscale));
  })();
};

export const markImageUpscaleAsSeen = async (): Promise<void> => {
  if (!isUserLoggedIn()) {
    return;
  }
  window.PMW.redux.store.dispatch(setHasUserSeenImageUpscale(true));
  try {
    await window.PMW.writeLocal('ai/markImageUpscaleAsSeen');
  } catch (e: unknown) {
    console.error(`Failed to mark image upscale as seen: ${e instanceof Error ? e.message : String(e)}`);
  }
};

export const shouldRemoveBackgroundForAiImageAction = (item: ImageItem): boolean => {
  return item.removeBackground.isBackgroundRemoved || item.hasTransparency === true;
};

export const shouldRemoveBackgroundForAiImageEdit = (item: ImageItem): boolean => {
  return shouldRemoveBackgroundForAiImageAction(item) || item.isMasked() || item.effects.hasEdgeTypeForStroke() || item.effects.doesEdgeEffectNeedClipping();
};

export const findImageItemForId = (uid: string): ImageItem | undefined => {
  const pages = window.posterEditor?.whiteboard?.pages;
  if (!pages) {
    return undefined;
  }
  const item = pages.getItemForId(uid);
  if (item?.isImage() || item?.isImageBackground()) {
    return item;
  }
  for (const pageHashedId of pages.pageOrder) {
    const page = pages.pagesHashMap[pageHashedId];
    for (const pageItem of Object.values(page?.items.itemsHashMap ?? {})) {
      if (pageItem.isSlideshow()) {
        const slide = pageItem.slides.slidesHashMap[uid];
        if (slide?.isImageSlide()) {
          return slide;
        }
      }
    }
  }
  return undefined;
};

export const upscaleImageItem = async (options: UpscaleImageItemOptions): Promise<void> => {
  const {uid, gaSource, aiProcessingStatus, teamCredits} = options;
  if (aiProcessingStatus !== AiProcessingStatus.NONE) {
    return;
  }
  trackPosterBuilderGA4Events(GA4EventName.AI_UPSCALE, {[GA4EventParamName.SOURCE]: gaSource});

  const targetItem = findImageItemForId(uid);
  if (!targetItem?.hashedFilename || targetItem.removeBackground.isBackgroundBeingRemoved) {
    return;
  }

  void markImageUpscaleAsSeen();

  if (targetItem.isUpscaled) {
    await setImageItemUpscaledState(targetItem, false);
    return;
  }

  if (window.PMW.isPAYGUser()) {
    void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_AI_UPSCALE);
    return;
  }

  if (teamCredits < getUpscaleCreditCost(shouldRemoveBackgroundForAiImageAction(targetItem))) {
    handleInsufficientCreditsForUpscale(options);
    return;
  }

  window.PMW.redux.store.dispatch(setItemAiProcessing({itemId: uid, status: AiProcessingStatus.UPSCALING}));
  startItemLoading(targetItem, {
    reason: ItemLoadingReason.AI_ANIMATE,
    onCanvasLoader: {type: ItemLoadingOverlayType.ANIMATED, variant: AnimatedItemLoadingOverlayVariant.GRADIENT},
  });

  const upscaledHashedFilename = targetItem.hashedFilename;
  try {
    await window.PMW.writeLocal('ai/upscaleUserImage', {
      hashedFilename: upscaledHashedFilename,
      shouldRemoveBackground: shouldRemoveBackgroundForAiImageAction(targetItem) ? '1' : '0',
      isBackgroundRemoved: targetItem.removeBackground.isBackgroundRemoved ? '1' : '0',
    });

    const currentItem = findImageItemForId(uid);
    if (!currentItem) {
      return;
    }
    if (currentItem.hashedFilename !== upscaledHashedFilename) {
      return;
    }

    await setImageItemUpscaledState(currentItem, true);
    void refreshTeamCreditsInStore();
    showMessageGrowl({
      text: window.i18next.t('pmwjs_image_upscaled'),
      type: GROWL_TYPE.NEUTRAL,
    });
    void holdItemForPendingVariantUploads(currentItem, upscaledHashedFilename);
  } catch (e: unknown) {
    console.error(`upscaleImageItem failed: ${e instanceof Error ? e.message : String(e)}`);
    showMessageGrowl({
      text: window.i18next.t('pmwjs_upscale_failed_try_again'),
      type: GROWL_TYPE.DANGER,
    });
  } finally {
    removeItemLoading(targetItem);
    window.PMW.redux.store.dispatch(clearItemAiProcessing(uid));
  }
};

export const holdItemForPendingVariantUploads = async (item: ImageItem, hashedFilename: string): Promise<void> => {
  item.beginAwaitingVariantUploads();
  try {
    await waitForUserImageVariantsReady(hashedFilename);
  } catch (e: unknown) {
    console.error(`Failed waiting for pending variant uploads of ${hashedFilename}: ${e instanceof Error ? e.message : String(e)}`);
  } finally {
    item.endAwaitingVariantUploads();
  }
};

const setImageItemUpscaledState = async (item: ImageItem, isUpscaled: boolean): Promise<void> => {
  if (!item.hashedFilename) {
    return;
  }
  await preloadImageForUpscaledState(item, isUpscaled);
  if (item.isImageSlide()) {
    await item.slideshow.updateSlideFromObject(item.uid, {isUpscaled});
    return;
  }
  await item.updateFromObject({isUpscaled});
};

const preloadImageForUpscaledState = async (item: ImageItem, isUpscaled: boolean): Promise<void> => {
  const previousIsUpscaled = item.isUpscaled;
  item.isUpscaled = isUpscaled;
  try {
    await loadImageAsync(await item.getImageUrl(Math.max(item.fabricObject.getScaledWidth(), item.fabricObject.getScaledHeight())));
  } catch {
    // best-effort warmup; the reinit that follows loads the image itself
  } finally {
    item.isUpscaled = previousIsUpscaled;
  }
};

export const updateOptionsPanelStickToBottom = (panelId: string, stickToBottom: boolean): void => {
  window.PMW.redux.store.dispatch(
    updatePanelProperties({
      panelId,
      stickToBottom,
    })
  );
};

export const updateOptionsPanelHeight = (panelId: string, height: string): void => {
  window.PMW.redux.store.dispatch(
    updatePanelSizeData({
      sizeDataId: panelId,
      sizeData: {
        height,
      },
    })
  );
};

export const updateOptionsPanelHasNoBorderRadius = (panelId: string, noBorderRadius: boolean): void => {
  window.PMW.redux.store.dispatch(
    updatePanelProperties({
      panelId,
      noBorderRadius,
    })
  );
};

export const updateOptionsPanelHasNoBorder = (panelId: string, noBorder: boolean): void => {
  window.PMW.redux.store.dispatch(
    updatePanelProperties({
      panelId,
      noBorder,
    })
  );
};

export const updateOptionsPanelMargin = (panelId: string, margin: number): void => {
  window.PMW.redux.store.dispatch(
    updatePanelProperties({
      panelId,
      margin,
    })
  );
};

export const updateOptionsPanelProperties = (panelId: string, hasSnapPoints: boolean): void => {
  window.PMW.redux.store.dispatch(
    updatePanelProperties({
      panelId,
      hasSnapPoints,
    })
  );
};

export const updateLastAddedTextColor = (color: RGBA): void => {
  if (window.posterEditor?.whiteboard) {
    window.posterEditor.whiteboard.lastAddedTextColor = color;
  }
};

export const updateLastDrawBrushColor = (color: RGBA): void => {
  if (window.posterEditor?.whiteboard) {
    window.posterEditor.whiteboard.drawing.lastDrawBrushColor = color;
  }
};

export const doesLayoutItemHasDualFonts = (type: LayoutTypes): boolean => {
  const layoutOptionsWithDualFonts = [
    LayoutTypes.BAND_LAYOUT_LEFT_ALIGN,
    LayoutTypes.BAND_LAYOUT_RIGHT_ALIGN,
    LayoutTypes.BAND_LAYOUT_CENTER_ALIGN,
    LayoutTypes.OFFSET_SPORTS_LAYOUT,
  ];

  return layoutOptionsWithDualFonts.includes(type);
};

export const openNewDesign = (): void => {
  const poster = window.posterEditor?.whiteboard;
  if (!poster) {
    return;
  }
  const posterName = poster.type.name;
  const isCustomSize = poster.type.isCustomSize();
  let redirectUrl = 'posterbuilder';
  if (isCustomSize) {
    redirectUrl = `posterbuilder/create?w=${poster.userWidth}&h=${poster.userHeight}&u=${poster.units}&cn=1&otp=true`;
  } else {
    redirectUrl = `posterbuilder/create?t=${posterName}&otp=true`;
  }
  if (isUserLoggedIn()) {
    void window.posterEditor?.whiteboard?.savePoster.save().then((): void => {
      window.open(window.PMW.util.site_url(redirectUrl), '_blank');
    });
  } else {
    showSaveReminderNotification();
    window.open(window.PMW.util.site_url(redirectUrl), '_blank');
  }
};

export const copyPoster = (versionId?: string): void => {
  void window.posterEditor?.whiteboard?.savePoster.save().then((): void => {
    const hashedID = window.posterEditor?.whiteboard?.hashedID;
    if (hashedID) {
      const url = versionId ? `posterbuilder/copy/${hashedID}?versionId=${versionId}` : `posterbuilder/copy/${hashedID}`;
      window.open(window.PMW.util.site_url(url), '_blank');
    }
  });
};

export const onClickOpenADesign = (): void => {
  void window.posterEditor?.whiteboard?.savePoster.save().then((): void => {
    window.open(window.PMW.util.site_url('posters/mine'));
  });
};

export const shouldDisplaySubmitAsTemplateButton = (): boolean => {
  return isUserDesigner() && !window.posterEditor?.whiteboard?.isPosterTemplate() && !window.PMW.isEmbedded();
};

export const submitAsTemplateCallback = (): void => {
  showLoading('submit-as-template', {
    text: window.i18next.t('pmwjs_loading'),
  });
  void window.posterEditor?.whiteboard?.savePoster.save().then((): void => {
    verifyUserEmailAndRedirectToTemplateSubmission(window.posterEditor?.whiteboard?.hashedID);
  });
};

const verifyUserEmailAndRedirectToTemplateSubmission = (hashedID: string | undefined): void => {
  void checkIfUserVerifiedEmailAndSendFirstVerificationEmail(hashedID ? `design_hid=${hashedID}` : '')
    .then(() => checkIfUserAllowedMultipageSubmissions())
    .then((isAllowedMultipageSubmissions: boolean) => {
      const hasMultiplePages = window.posterEditor?.whiteboard?.pages.hasMultiplePages();
      if (hashedID && hasMultiplePages && !isAllowedMultipageSubmissions) {
        openMultipageTemplateSubmissionUnavailableModal({
          posterHashedId: hashedID,
          onClose() {
            hideLoading('submit-as-template');
          },
        });
      } else {
        redirectToSubmitAsTemplate(hashedID);
      }
    })
    .catch((error: ErrorEmailVerificationRequired) => {
      if (error.data) {
        openUserEmailVerificationModal({
          userEmail: error.data.userEmail,
          sniperLink: error.data.sniperLink,
          onVerifiedCB: () => {
            redirectToSubmitAsTemplate(hashedID);
          },
          onClose: () => {
            hideLoading('submit-as-template');
          },
          pollVerification: pollHasUserVerifiedEmail,
          sendVerification: () => sendUserVerificationEmail({design_hid: hashedID}),
        });
      } else {
        openUserEmailVerificationErrorModal({errorMsg: error.message});
      }
    });
};

const redirectToSubmitAsTemplate = (hashedID: string | undefined): void => {
  if (hashedID) {
    window.location.href = window.PMW.util.site_url(`sell/submit/${hashedID}`);
  }
};

export const showEditorNotificationMessageGrowl = (text: string): void => {
  showMessageGrowl({
    text,
    interval: 2000,
    type: GROWL_TYPE.NEUTRAL,
    showCloseIcon: false,
  });
};

export const isScrollMode = (): boolean => {
  return !isSinglePageMode();
};

export const isSinglePageMode = (): boolean => {
  return selectIsSinglePageLayout(window.PMW.redux.store.getState());
};

export const openEditorPosterTools = (): void => {
  if (window.posterEditor?.whiteboard?.hashedID) {
    window.open(window.PMW.util.site_url(`posterbuilder/openPosterTools/${window.posterEditor.whiteboard.hashedID}`));
  } else {
    openMessageModal({
      text: 'No hash ID. New poster that has not been saved yet?',
    });
  }
};

export const isSidebarSmall = (): boolean => {
  return window.PMW.redux.store.getState().posterEditor?.isSidebarSmall ?? false;
};

export const updateSidebarOpenState = (newState = true): void => {
  window.PMW.redux.store.dispatch(updateSidebarState(newState));
};

export const doesPosterHaveAudios = (): boolean => {
  const audioHash = window.posterEditor?.whiteboard?.getCurrentPage().audioClips.audioItemsHashMap;
  return !(!audioHash || Object.keys(audioHash).length === 0);
};

export const logoutUserFromEmbeddedEditor = async (): Promise<void> => {
  window.PMW.showLoading('embeddedEditorLogout', {text: window.i18next.t('pmwjs_logging_out')});
  try {
    await window.PMW.writeLocal('authenticate/asyncLogout');
    window.location.reload();
  } catch (e) {
    console.error(e);
  } finally {
    window.PMW.hideLoading('embeddedEditorLogout');
  }
};

const EDITOR_TEMPLATES_POPUP_COOKIE = 'pmwetp';
const EDITOR_TEMPLATES_POPUP_PULSATION_DEADLINE = '2025-05-13';
export const showTemplatesPopupPulsatingDot = (): boolean => {
  if (!isUserLoggedIn()) {
    return false;
  }
  const targetDate = new Date(EDITOR_TEMPLATES_POPUP_PULSATION_DEADLINE);
  if (hasDatePassed2(targetDate)) {
    return false;
  }
  const value = readCookie(EDITOR_TEMPLATES_POPUP_COOKIE);

  if (!value) {
    return true;
  }

  const hasSeenValue = Number(value);
  return !hasSeenValue;
};

export const toggleTemplatesPopupOnBoardingToSeen = (): void => {
  setCookie(EDITOR_TEMPLATES_POPUP_COOKIE, '1', 180);
};

const TIMELINE_ONBOARDING_COOKIE = 'pmwto';
const TIMELINE_ONBOARDING_DEADLINE = '2025-03-29';
export const showTimelineOnBoarding = (): boolean => {
  if (!isUserLoggedIn()) {
    return false;
  }

  const targetDate = new Date(TIMELINE_ONBOARDING_DEADLINE);
  if (hasDatePassed2(targetDate)) {
    return false;
  }

  const value = readCookie(TIMELINE_ONBOARDING_COOKIE);

  if (!value) {
    return true;
  }

  const hasSeenValue = Number(value);
  return !hasSeenValue;
};

export const toggleTimelineOnBoardingToSeen = (): void => {
  setCookie(TIMELINE_ONBOARDING_COOKIE, '1', 180);
};

const VERSION_HISTORY_PULSE_COOKIE = 'pmwvhp';
const VERSION_HISTORY_PULSE_DEADLINE = '2026-03-30';
const POSTER_ANIMATION_PULSE_COOKIE = 'pmwpap';
const POSTER_ANIMATION_PULSE_DEADLINE = '2027-01-15';

const shouldShowPulse = (cookieName: string, deadline: string): boolean => {
  if (!isUserLoggedIn()) {
    return false;
  }
  if (hasDatePassed2(new Date(deadline))) {
    return false;
  }
  return !readCookie(cookieName);
};

export const showVersionHistoryPulse = (): boolean => {
  return shouldShowPulse(VERSION_HISTORY_PULSE_COOKIE, VERSION_HISTORY_PULSE_DEADLINE);
};

export const markVersionHistoryPulseAsSeen = (): void => {
  setCookie(VERSION_HISTORY_PULSE_COOKIE, '1', 180);
};

export const showPosterAnimationPulse = (): boolean => {
  return shouldShowPulse(POSTER_ANIMATION_PULSE_COOKIE, POSTER_ANIMATION_PULSE_DEADLINE);
};

export const markPosterAnimationPulseAsSeen = (): void => {
  setCookie(POSTER_ANIMATION_PULSE_COOKIE, '1', 180);
};

export const isTemplatesPopupHidden = (): boolean => {
  if (isUserStudent()) {
    return true;
  }
  return window.posterEditor?.whiteboard?.mode.details.type === PosterModeType.EDIT && isInReviewOrGalleryTemplate();
};

export const isMyDesignsHidden = (): boolean => {
  return !window.PMW.isUserLoggedIn();
};

export const isCustomPosterType = (): boolean => {
  const posterType = window.posterEditor?.whiteboard?.type;
  return posterType?.name === POSTER_CUSTOM || posterType?.name === POSTER_CUSTOM_PRINT;
};

export const getFloatyEditorHorizontalBuffer = (): number => {
  if (isEditorMobileVariant()) {
    return 0;
  }

  const isSidebarSmall = window.PMW.redux.store.getState().posterEditor.isSidebarSmall;
  if (isSidebarSmall) {
    return EDITOR_RIGHT_MARGIN_SMALL + EDITOR_LEFT_MARGIN;
  }

  return EDITOR_RIGHT_MARGIN + EDITOR_LEFT_MARGIN;
};

const getPanelToShowForAddItemModal = (feature: FeatureToShowOnLoad): AddItemOptions => {
  switch (feature) {
    case FeatureToShowOnLoad.TEMPLATE_POPUP:
      return AddItemOptions.TEMPLATE;
    case FeatureToShowOnLoad.TEXT_POPUP:
      return AddItemOptions.TEXT;
    case FeatureToShowOnLoad.AI_SUBTITLE:
      return AddItemOptions.AI_SUBTITLES;
    case FeatureToShowOnLoad.AI_VOICE:
      return AddItemOptions.AI_VOICE;
    case FeatureToShowOnLoad.AI_IMAGE:
      return AddItemOptions.AI_IMAGE;
    case FeatureToShowOnLoad.AI_VIDEO:
      return AddItemOptions.AI_VIDEO;
    case FeatureToShowOnLoad.AI_TEXT_ART:
      return AddItemOptions.AI_MENU;
    default:
      return AddItemOptions.DEFAULT;
  }
};

export const handleFeatureToShowForMobile = (feature: FeatureToShowOnLoad): void => {
  if (!isEditorMobileVariant()) {
    return;
  }

  if (feature === FeatureToShowOnLoad.NONE) {
    return;
  }

  if (feature === FeatureToShowOnLoad.AI_REMOVE_BG) {
    openAiRemoveBgModal();
    return;
  }

  if (feature === FeatureToShowOnLoad.MENU_MAKER || feature === FeatureToShowOnLoad.STICKERS || feature === FeatureToShowOnLoad.AI_EDIT) {
    return;
  }

  openPosterAddItemsModal({panelToShowOnOpen: getPanelToShowForAddItemModal(feature)});
};

export const onMoveUpPage = (pageId: string): void => {
  window.posterEditor?.whiteboard?.pages.movePageUp(pageId);
  scrollToPage(pageId);
};

export const onMoveDownPage = (pageId: string): void => {
  window.posterEditor?.whiteboard?.pages.movePageDown(pageId);
  scrollToPage(pageId);
};

export const onDeletePage = (pageId: string): void => {
  window.posterEditor?.whiteboard?.pages.removePage(pageId).catch((e) => {
    console.error(e);
  });
};

export const isFabricObjectOnPageCanvas = (fabricObject: FabricObject, pageHashedId: string): boolean => {
  const targetBackdrop = findPageCanvasBackdropElement(pageHashedId);
  if (!targetBackdrop) {
    return false;
  }

  const canvas = fabricObject.canvas;
  if (!canvas) {
    return false;
  }

  const pages = window.posterEditor?.whiteboard?.pages.pagesHashMap;
  if (!pages) {
    return false;
  }

  const ownerPage = Object.values(pages).find((page) => page.fabricCanvas === canvas);
  if (!ownerPage) {
    return false;
  }

  const ownerBackdrop = findPageCanvasBackdropElement(ownerPage.hashedID);
  if (!ownerBackdrop) {
    return false;
  }

  const ownerBackdropRect = ownerBackdrop.getBoundingClientRect();
  const targetBackdropRect = targetBackdrop.getBoundingClientRect();
  const posterScale = ownerPage.poster.scaling.scale;

  const boundingRect = fabricObject.getBoundingRect();
  const itemLeft = ownerBackdropRect.left + boundingRect.left * posterScale;
  const itemTop = ownerBackdropRect.top + boundingRect.top * posterScale;
  const itemRight = itemLeft + boundingRect.width * posterScale;
  const itemBottom = itemTop + boundingRect.height * posterScale;

  return itemLeft < targetBackdropRect.right && itemRight > targetBackdropRect.left && itemTop < targetBackdropRect.bottom && itemBottom > targetBackdropRect.top;
};

export const moveItemToDifferentPage = async (previousPage: Page, newPage: Page, item: ItemType): Promise<string | undefined> => {
  const controlsWrapperForItemPreviousPage = document.querySelector(`#${CSS.escape(previousPage.hashedID)}.${CUSTOM_CONTROLS_WRAPPER}`);
  const controlsWrapperForItemNewPage = document.querySelector(`#${CSS.escape(newPage.hashedID)}.${CUSTOM_CONTROLS_WRAPPER}`);

  if (!controlsWrapperForItemPreviousPage || !controlsWrapperForItemNewPage) {
    return undefined;
  }

  if (!newPage.items.addItems.canFitAdditionalItems(1)) {
    return undefined;
  }

  showLoading('moveItemToDifferentPage', {text: window.i18next.t('pmwjs_moving_item')});

  const groupRecordToCarryOver = item.groupId ? previousPage.items.groups.groupsHashMap[item.groupId] : undefined;

  try {
    const previousPageControlsWrapperTop = parseFloat((controlsWrapperForItemPreviousPage as HTMLDivElement).style.top);
    const newPageControlsWrapperTop = parseFloat((controlsWrapperForItemNewPage as HTMLDivElement).style.top);

    const newTop = item.fabricObject.getY() - (newPageControlsWrapperTop - previousPageControlsWrapperTop) / newPage.poster.scaling.scale;

    const uidForNewItem = uuidv4();

    const itemToAdd = await createItemFromObject(newPage, {
      ...item.toObject(),
      uid: uidForNewItem,
      left: item.fabricObject.getX(),
      top: newTop,
    });

    // Same add path as paste: a longer item stretches the destination's last background instead of leaving a gap after it.
    const wasAdded = await newPage.items.addItems.addItemToPage(itemToAdd, {undoable: false, selectOnAdd: false, preserveItemTiming: true, addType: ItemAddType.PAGE_MOVE});
    if (!wasAdded) {
      return undefined;
    }

    if (item.groupId && groupRecordToCarryOver) {
      newPage.items.groups.updateGroup(item.groupId, groupRecordToCarryOver);
    }

    transferItemLoadingStates(item, itemToAdd);

    previousPage.items.removeItem(item.uid, false);

    return uidForNewItem;
  } finally {
    hideLoading('moveItemToDifferentPage');
  }
};

export const initComWithIframe = (): void => {
  window.addEventListener('message', (e): void => {
    if (e && typeof e.data === 'string') {
      const parts = e.data.split('~');
      // length === 2 such that 1 = key, 2 = value.
      if (parts.length === 2) {
        const key = parts[0];
        const value = parts[1];

        switch (key) {
          case 'Mode':
            if (value === 'ReturnURL') {
              window.PMW.redux.store.dispatch(updateIsEmbeddedExportMode(true));
            }
            break;

          default:
            break;
        }
      }
    }
  });
  window.parent.postMessage(EmbeddedEditorMessageType.RENDER_CLOSE_BTN, '*');
  window.parent.postMessage(EmbeddedEditorMessageType.READY_FOR_MODE, '*');
};

/**
 * Tears down the canvas-pick mode currently in progress, if any, without running its callback.
 * A mode owns the cursor, an escape binding and a document-level overlay listener, none of which
 * belong to a React tree — so the editor shell must cancel it on teardown or they outlive the
 * canvases they close over. Safe to call when no mode is active.
 */
export const cancelCanvasCursorMode = (): void => {
  activeCursorModeDisposer?.();
};

/**
 * Enters a one-shot canvas-pick mode: the cursor becomes `cursor` until the user clicks a canvas,
 * clicks off it, or presses escape, at which point `callback` fires with the ending event.
 * Only one mode can be active — starting a second cancels the first, since they would otherwise
 * both hold the cursor and fight over the single escape binding.
 */
export const changeCanvasCursor = (cursor: string, callback?: (event?: CursorModeEvent) => void): void => {
  const pages = window.posterEditor?.whiteboard?.pages.pagesHashMap;
  if (!pages) {
    return;
  }

  cancelCanvasCursorMode();

  const allCanvases = Object.values(pages).map((page) => page.fabricCanvas as Canvas);
  const detachers: (() => void)[] = [];

  const applyCursorTimeout = window.setTimeout(() => {
    const cursorValue = `url("${window.PMW.util.asset_url(cursor)}") ${CURSOR_HOTSPOT} , auto`;
    for (const canvas of allCanvases) {
      applyCanvasCursor(canvas, cursorValue);
    }
  }, CURSOR_APPLY_DELAY_MS);

  const endCursorMode = (): void => {
    window.clearTimeout(applyCursorTimeout);
    if (activeCursorModeDisposer === endCursorMode) {
      activeCursorModeDisposer = null;
    }
    for (const detach of detachers) {
      detach();
    }
    detachers.length = 0;
    for (const canvas of allCanvases) {
      applyCanvasCursor(canvas, 'default');
    }
  };

  const onCursorModeEnded = (event?: CursorModeEvent): void => {
    endCursorMode();
    if (callback) {
      callback(event);
    }
  };

  for (const canvas of allCanvases) {
    canvas.on('mouse:down', onCursorModeEnded);
    detachers.push((): void => {
      canvas.off('mouse:down', onCursorModeEnded);
    });
  }

  const cursorOverlay = document.getElementById(CURSOR_OVERLAY_CONTAINER_ID);
  if (cursorOverlay) {
    cursorOverlay.addEventListener('click', onCursorModeEnded);
    detachers.push((): void => {
      cursorOverlay.removeEventListener('click', onCursorModeEnded);
    });
  }

  if (deviceType !== DEVICE_TYPE.MOBILE) {
    Mousetrap.bind('escape', onCursorModeEnded);
    detachers.push((): void => {
      Mousetrap.unbind('escape');
    });
  }

  activeCursorModeDisposer = endCursorMode;
};

const disablePastingStyles = (): void => {
  window.PMW.redux.store.dispatch(updateCopiedStylesState(undefined));
};

export const openLayersPanel = (source: LayersGA4Source): void => {
  trackPosterBuilderGA4Events(GA4EventName.LAYERS_CLICKED, {[GA4EventParamName.SOURCE]: source});
  if (isEditorMobileVariant()) {
    openLayersModal();
    return;
  }
  window.PMW.redux.store.dispatch(updateLayersPanelState(true));
};

export const toggleLayersPanel = (): void => {
  const isOpen = window.PMW.redux.store.getState().posterEditor.isLayersPanelOpen;
  if (!isOpen) {
    trackPosterBuilderGA4Events(GA4EventName.LAYERS_CLICKED, {[GA4EventParamName.SOURCE]: LayersGA4Source.SHORTCUT});
  }
  window.PMW.redux.store.dispatch(updateLayersPanelState(!isOpen));
};

export const onClickOpenVersionHistoryPanel = async (): Promise<void> => {
  await window.posterEditor?.whiteboard?.savePoster.save({}, false);

  window.posterEditor?.whiteboard?.clearSelection();
  window.PMW.redux.store.dispatch(updateVersionHistoryModeState(true));

  const state = window.PMW.redux.store.getState();
  if (state.posterEditor.isVideo) {
    void window.posterEditor?.whiteboard?.pause();
    window.posterEditor?.whiteboard?.updateIsTimelineMode(false);
  }

  if (isEditorMobileVariant()) {
    onPosterEditorVersionHistoryModal();
  }
};

export const closeVersionHistoryPanel = async (restoreOriginalVersion = true): Promise<void> => {
  try {
    if (window.PMW.redux.store.getState().posterEditor.versionHistorySelectedVersion !== null) {
      return;
    }

    window.PMW.redux.store.dispatch(updateVersionHistoryModeState(false));

    const posterHashId = window.posterEditor?.whiteboard?.hashedID;
    if (posterHashId && restoreOriginalVersion) {
      await loadPosterVersionFromHashedId(posterHashId, undefined);
    }
  } catch (err) {
    console.error('Failed to close version history panel:', err);
  }
};

export const showVersionHistoryOnBoarding = async (): Promise<boolean> => {
  if (!isUserLoggedIn()) {
    return false;
  }

  try {
    const response = (await window.PMW.readLocal('user/hasUserSeenVersionHistoryOnboarding')) as UserHasSeenResponse;
    return !response.hasSeen;
  } catch (error) {
    console.error('Error checking version history onboarding status:', error);
    return false;
  }
};

export const markVersionHistoryOnboardingAsSeen = async (): Promise<void> => {
  if (!isUserLoggedIn()) {
    return;
  }

  try {
    await window.PMW.writeLocal('user/updateUserHasSeenVersionHistoryOnboarding');
  } catch (error) {
    console.error('Error updating version history onboarding status:', error);
  }
};

const getPosterEditorOnboardingToShow = (): PosterEditorOnboarding | null => {
  return window.posterEditor?.config?.onboarding ?? null;
};

const clearPosterEditorOnboardingToShow = (): void => {
  if (window.posterEditor?.config) {
    window.posterEditor.config.onboarding = null;
  }
};

const markPosterEditorOnboardingAsSeen = async (updateHasSeenEndpoint: string): Promise<void> => {
  if (!isUserLoggedIn()) {
    return;
  }

  clearPosterEditorOnboardingToShow();

  try {
    await window.PMW.writeLocal(updateHasSeenEndpoint);
  } catch (error) {
    console.error('Error updating onboarding seen status:', error);
  }
};

export const showLayersOnboarding = (): boolean => {
  return getPosterEditorOnboardingToShow() === PosterEditorOnboarding.LAYERS;
};

export const markLayersOnboardingAsSeen = async (): Promise<void> => {
  await markPosterEditorOnboardingAsSeen('user/updateUserHasSeenLayersOnboarding');
};

export const showPosterAnimationOnboarding = (): boolean => {
  return getPosterEditorOnboardingToShow() === PosterEditorOnboarding.AI_POSTER_ANIMATION;
};

export const markPosterAnimationOnboardingAsSeen = async (): Promise<void> => {
  await markPosterEditorOnboardingAsSeen('user/updateUserHasSeenAiPosterAnimationOnboarding');
};

export const showItemAnimationOnboarding = async (): Promise<boolean> => {
  if (!isUserLoggedIn()) {
    return false;
  }

  try {
    const response = (await window.PMW.readLocal('user/hasUserSeenAiItemAnimationOnboarding')) as UserHasSeenResponse;
    return !response.hasSeen;
  } catch (error) {
    console.error('Error checking animate with ai onboarding status:', error);
    return false;
  }
};

export const markItemAnimationOnboardingAsSeen = async (): Promise<void> => {
  if (!isUserLoggedIn()) {
    return;
  }

  try {
    await window.PMW.writeLocal('user/updateUserHasSeenAiItemAnimationOnboarding');
  } catch (error) {
    console.error('Error updating animate with ai onboarding status:', error);
  }
};

export const showMultipageOnboarding = (): boolean => {
  return getPosterEditorOnboardingToShow() === PosterEditorOnboarding.MULTIPAGE;
};

export const markMultipageOnboardingAsSeen = async (): Promise<void> => {
  await markPosterEditorOnboardingAsSeen('user/updateUserHasSeenMultipageOnboarding');
};

export interface PosterVersionData {
  versionId: string;
  createdOn: number;
  creatorName: string | null;
}

/** Mirrored from Poster::ERR_POSTER_ACCESS_DENIED. */
const POSTER_ACCESS_DENIED_ERROR_CODE = 'poster_access_denied';

export enum PosterVersionsOutcome {
  LOADED = 'loaded',
  ACCESS_DENIED = 'accessDenied',
  FAILED = 'failed',
}

export interface PosterVersionsResult {
  outcome: PosterVersionsOutcome;
  versions: PosterVersionData[];
}

const isAccessDeniedError = (error: unknown): boolean => {
  return error instanceof Error && 'code' in error && error.code === POSTER_ACCESS_DENIED_ERROR_CODE;
};

export const fetchPosterVersions = async (posterHashedId: string): Promise<PosterVersionsResult> => {
  try {
    const versions = (await window.PMW.readLocal('poster/getPosterVersionData', {
      hashedID: posterHashedId,
    })) as PosterVersionData[];

    return {outcome: PosterVersionsOutcome.LOADED, versions};
  } catch (err) {
    if (isAccessDeniedError(err)) {
      return {outcome: PosterVersionsOutcome.ACCESS_DENIED, versions: []};
    }

    console.error(err);
    return {outcome: PosterVersionsOutcome.FAILED, versions: []};
  }
};

export const exitMobilePageSelectionMode = (): void => {
  window.PMW.redux.store.dispatch(closeModal(MODALS_IDS.POSTER_EDITOR_PAGE_OPTIONS_MODAL));
  window.PMW.redux.store.dispatch(setMobilePageSelectionModeState(MobilePageSelectionModeState.INACTIVE));

  const poster = window.posterEditor?.whiteboard;
  if (!poster) {
    return;
  }

  const fitToScreenScale = poster.getCurrentPage().poster.scaling.getFitToScreenScale();
  poster.scaling.setZoom(fitToScreenScale);
};
